import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useDocumentTitle } from '@/shared/hooks/use-document-title'
import { useBreadcrumb } from '@/shared/context/BreadcrumbContext'
import { useAuth } from '@/auth'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table'
import {
  ArrowLeft, Eye, PlayCircle, CheckCircle2, TrendingUp, TrendingDown, Target,
  FileSpreadsheet, QrCode, RotateCcw, Trash2, Copy, RefreshCw,
} from 'lucide-react'
import { toast } from 'sonner'
import {
  useReporteDatos, useEliminarRespuesta, useReiniciarEncuesta,
  useExportarExcelReporte, useDescargarQRReporte,
} from '../hooks/useReportes'
import { MetricCard } from '../components/reportes/MetricCard'

export function ReporteDatosPage() {
  useDocumentTitle('Reporte de Datos')
  const { id } = useParams<{ id: string }>()
  const encuestaId = Number(id)
  const navigate = useNavigate()
  const { user } = useAuth()
  const canEdit = user?.is_superuser || ['admin', 'encuestas_admin'].some(g => user?.groups?.includes(g))

  const { setOverride, clearOverride } = useBreadcrumb()
  const [page, setPage] = useState(1)

  const { data, isLoading, refetch } = useReporteDatos(encuestaId, { page, page_size: 25 })

  useEffect(() => {
    if (data?.encuesta_titulo) {
      setOverride(`/encuestas/reportes/${id}`, data.encuesta_titulo)
    }
    return () => { clearOverride(`/encuestas/reportes/${id}`) }
  }, [data?.encuesta_titulo, id, setOverride, clearOverride])
  const eliminarMutation = useEliminarRespuesta(encuestaId)
  const reiniciarMutation = useReiniciarEncuesta()
  const excelMutation = useExportarExcelReporte()
  const qrMutation = useDescargarQRReporte()

  const handleEliminar = async (sesionId: number) => {
    if (confirm('¿Eliminar esta respuesta?')) {
      await eliminarMutation.mutateAsync(sesionId)
    }
  }

  const handleReiniciar = async () => {
    if (confirm('¿Eliminar TODAS las respuestas de esta encuesta? Esta acción no se puede deshacer.')) {
      await reiniciarMutation.mutateAsync(encuestaId)
      refetch()
    }
  }

  const handleCopyUrl = async () => {
    if (data?.url_publica) {
      const url = `${window.location.origin}${data.url_publica}`
      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(url)
        } else {
          throw new Error('clipboard not available')
        }
      } catch {
        setTimeout(() => {
          const input = document.createElement('input')
          input.value = url
          input.style.position = 'fixed'
          input.style.left = '-9999px'
          document.body.appendChild(input)
          input.focus()
          input.select()
          document.execCommand('copy')
          document.body.removeChild(input)
        }, 100)
      }
      toast.success('URL copiada')
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    )
  }

  if (!data) return null

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/encuestas/reportes')}>
            <ArrowLeft className="mr-2 h-4 w-4" /> Volver
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">{data.encuesta_titulo}</h1>
            <div className="flex items-center gap-2 mt-1">
              <Badge className={data.encuesta_activa ? 'badge-gradient' : ''} variant={data.encuesta_activa ? 'default' : 'secondary'}>
                {data.encuesta_activa ? 'Activa' : 'Inactiva'}
              </Badge>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            <RefreshCw className="mr-2 h-4 w-4" /> Actualizar
          </Button>
          <Button variant="outline" size="sm" onClick={() => excelMutation.mutate(encuestaId)}>
            <FileSpreadsheet className="mr-2 h-4 w-4" /> Excel
          </Button>
          <Button variant="outline" size="sm" onClick={() => qrMutation.mutate(encuestaId)}>
            <QrCode className="mr-2 h-4 w-4" /> QR
          </Button>
          {canEdit && (
            <Button variant="destructive" size="sm" onClick={handleReiniciar}>
              <RotateCcw className="mr-2 h-4 w-4" /> Reiniciar
            </Button>
          )}
        </div>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <MetricCard label="Visualizaciones" value={data.total_visualizaciones} icon={Eye} />
        <MetricCard label="Inicios" value={data.total_inicios} icon={PlayCircle} />
        <MetricCard label="Respuestas" value={data.total_respuestas} icon={CheckCircle2} />
        <MetricCard label="Conversión" value={`${data.tasa_conversion.toFixed(1)}%`} icon={TrendingUp} />
        <MetricCard label="Finalización" value={`${data.tasa_finalizacion.toFixed(1)}%`} icon={Target} />
        <MetricCard label="Abandono" value={`${data.tasa_abandono.toFixed(1)}%`} icon={TrendingDown} />
      </div>

      {/* URL pública */}
      {data.total_respuestas === 0 && data.url_publica && (
        <Card className="p-4 border border-gray-300 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">URL pública de la encuesta</p>
              <p className="text-sm text-muted-foreground">{window.location.origin}{data.url_publica}</p>
            </div>
            <Button variant="outline" size="sm" onClick={handleCopyUrl}>
              <Copy className="mr-2 h-4 w-4" /> Copiar
            </Button>
          </div>
        </Card>
      )}

      {/* Table */}
      <Card className="border border-gray-300 dark:border-gray-700">
        {data.results.length > 0 ? (
          <div className="p-6 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-100 dark:bg-[hsl(220,15%,16%)]">
                  <TableHead>Fecha</TableHead>
                  <TableHead>Paciente</TableHead>
                  {canEdit && <TableHead>IP / Dispositivo</TableHead>}
                  <TableHead className="text-center">Tiempo</TableHead>
                  {data.campos.map((campo) => (
                    <TableHead key={campo.id} className="min-w-[120px]">{campo.etiqueta}</TableHead>
                  ))}
                  {data.mostrar_resultados && <TableHead>Resultado</TableHead>}
                  {canEdit && <TableHead className="text-right">Acciones</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.results.map((sesion) => (
                  <TableRow key={sesion.id}>
                    <TableCell className="text-sm whitespace-nowrap">
                      {sesion.fecha_completado ? (
                        <>
                          <div>{sesion.fecha_completado.split(' ')[0]}</div>
                          <div className="text-muted-foreground text-xs">{sesion.fecha_completado.split(' ')[1]}</div>
                        </>
                      ) : '-'}
                    </TableCell>
                    <TableCell className="text-sm">{sesion.nombre_paciente || '-'}</TableCell>
                    {canEdit && (
                      <TableCell className="text-xs">
                        <div>{sesion.ip_address}</div>
                        <div className="text-muted-foreground">{sesion.dispositivo}</div>
                      </TableCell>
                    )}
                    <TableCell className="text-center text-sm">
                      {sesion.tiempo_segundos ? `${sesion.tiempo_segundos}s` : '-'}
                    </TableCell>
                    {data.campos.map((campo) => (
                      <TableCell key={campo.id} className="text-sm max-w-[150px] truncate">
                        {sesion.respuestas[String(campo.id)] || '-'}
                      </TableCell>
                    ))}
                    {data.mostrar_resultados && (
                      <TableCell>
                        {sesion.resultado_titulo ? (
                          <Badge variant="outline">{sesion.resultado_titulo}</Badge>
                        ) : '-'}
                      </TableCell>
                    )}
                    {canEdit && (
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" onClick={() => handleEliminar(sesion.id)}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {/* Pagination */}
            {data.total_pages > 1 && (
              <div className="flex items-center justify-between mt-6">
                <div className="text-sm text-muted-foreground">
                  Mostrando {(page - 1) * 25 + 1} - {Math.min(page * 25, data.count)} de {data.count}
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setPage(p => p - 1)} disabled={page === 1}>
                    Anterior
                  </Button>
                  <div className="text-sm">Página {page} de {data.total_pages}</div>
                  <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={page === data.total_pages}>
                    Siguiente
                  </Button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="p-12 text-center">
            <CheckCircle2 className="mx-auto h-12 w-12 text-teal-500" />
            <h3 className="mt-4 text-lg font-semibold">Sin respuestas aún</h3>
            <p className="mt-2 text-sm text-muted-foreground">Las respuestas completadas aparecerán aquí</p>
          </div>
        )}
      </Card>
    </div>
  )
}
